import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.png'; // Replace with your logo path

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled components
const NavbarContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  width: 100%;
  background: ${props => props.scrolled ? 
    'rgba(15, 15, 26, 0.85)' : 
    'rgba(15, 15, 26, 0.5)'};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: ${props => props.scrolled ? 
    '1px solid rgba(255, 255, 255, 0.1)' : 
    '1px solid transparent'};
  z-index: 1000;
  padding: 0.8rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #ff4d8d, #6c28d9);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const LogoImage = styled(motion.img)`
  height: 50px;
  width: auto;
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.3));
  
  @media (max-width: 768px) {
    height: 40px;
  }
`;

const ProjectName = styled(motion.span)`
  font-size: 1.5rem;
  font-weight: 800;
  margin-left: 0.75rem;
  background: linear-gradient(45deg, #ff4d8d, #6c28d9, #00c6fb);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const NavLinks = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  
  @media (max-width: 768px) {
    display: ${props => props.isMenuOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background: rgba(15, 15, 26, 0.95);
    backdrop-filter: blur(15px);
    -webkit-backdrop-filter: blur(15px);
    flex-direction: column;
    padding: 1.5rem;
    gap: 1.2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const NavButton = styled(motion(Link))`
  padding: 0.7rem 1.5rem;
  border-radius: 50px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  background: ${props => props.isActive ? 
    'linear-gradient(45deg, rgba(108, 40, 217, 0.2), rgba(255, 77, 141, 0.2))' : 
    'transparent'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.7s ease;
  }
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 5px 15px rgba(108, 40, 217, 0.3);
    
    &::before {
      left: 100%;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    padding: 1rem;
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  background: ${props => props.isMenuOpen ? 
    'rgba(108, 40, 217, 0.2)' : 
    'transparent'};
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ConnectButton = styled(motion.button)`
  padding: 0.7rem 1.5rem;
  border-radius: 50px;
  background: linear-gradient(45deg, #6c28d9, #ff4d8d);
  color: white;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(108, 40, 217, 0.3);
  animation: ${pulse} 2s infinite;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(108, 40, 217, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 1rem;
    padding: 1rem;
  }
`;

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu when route changes
    setIsMenuOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <NavbarContainer
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.6, 0.05, 0.1, 0.9] }}
        scrolled={scrolled ? 1 : 0}
      >
        <LogoContainer to="/">
          <LogoImage
            src={logo}
            alt="RoseCity Web3 Logo"
            whileHover={{ rotate: 5, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          />
          <ProjectName
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            RoseCity Web3
          </ProjectName>
        </LogoContainer>

        <NavLinks
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          isMenuOpen={isMenuOpen}
        >
          <NavButton 
            to="/about"
            isActive={location.pathname === '/about'}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            About Us
          </NavButton>
          <NavButton 
            to="/support"
            isActive={location.pathname === '/support'}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Support
          </NavButton>
        </NavLinks>

        <MobileMenuButton
          onClick={toggleMenu}
          isMenuOpen={isMenuOpen}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
      </NavbarContainer>
    </>
  );
}

export default Navbar;